import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay';
import { FormattedMessage } from 'react-intl';
import get from 'lodash.get';

import styles from './PdpSharedDealerInfo.scss';

const formatResponseTime = value => {
    const responseTime = parseInt(value, 10);
    if (isNaN(responseTime)) {
        return null;
    }

    if (responseTime > 168) {
        return (
            <FormattedMessage id="PdpSharedDealerInfo.response_time_week" defaultMessage="A week" />
        );
    }

    if (responseTime > 48) {
        return (
            <FormattedMessage
                id="PdpSharedDealerInfo.response_time_several_days"
                defaultMessage="Several days"
            />
        );
    }

    if (responseTime > 23) {
        return (
            <FormattedMessage
                id="PdpSharedDealerInfo.response_time_few_days"
                defaultMessage="1 to 2 days"
            />
        );
    }

    return (
        <FormattedMessage
            id="PdpSharedDealerInfo.response_time_value"
            defaultMessage="{responseTime, plural,
              =0 {<1 hour}
              one {1 hour}
              other {{responseTime} hours}
            }"
            values={{ responseTime }}
        />
    );
};

function PdpSharedDealerInfoComponent({ seller }) {
    const dealerSince = new Date(get(seller, 'agreementStart')).getUTCFullYear();
    const dealerLocation = get(seller, 'shippingAddress.displayCityStateCountry');
    const responseTime = formatResponseTime(get(seller, 'analytics.metrics.responseTime.value'));
    const tradeAssociations = get(seller, 'tradeAssociations');

    return (
        <Fragment>
            {dealerSince && (
                <div data-tn="dealer-since">
                    <FormattedMessage
                        id="PdpSharedDealerInfo.since"
                        defaultMessage="1stdibs seller since {dealerSince}"
                        values={{ dealerSince }}
                    />
                </div>
            )}
            {responseTime && (
                <div data-tn="dealer-response-time">
                    <FormattedMessage
                        id="PdpSharedDealerInfo.response_time"
                        defaultMessage="Typical response time: {responseTime}"
                        values={{ responseTime }}
                    />
                </div>
            )}
            {dealerLocation && (
                <div data-tn="dealer-location">
                    <FormattedMessage
                        id="PdpSharedDealerInfo.location"
                        defaultMessage="Located in {dealerLocation}"
                        values={{ dealerLocation }}
                    />
                </div>
            )}
            {tradeAssociations.length ? (
                <div className={styles.associations} data-tn="dealer-associations">
                    <FormattedMessage
                        id="PdpSharedDealerInfo.associations"
                        defaultMessage="Associations:"
                    />
                    {tradeAssociations.map(({ serviceId, displayName }) => {
                        return <div key={serviceId}>{displayName}</div>;
                    })}
                </div>
            ) : null}
        </Fragment>
    );
}

PdpSharedDealerInfoComponent.propTypes = {
    seller: PropTypes.object.isRequired,
};

export const PdpSharedDealerInfo = createFragmentContainer(PdpSharedDealerInfoComponent, {
    seller: graphql`
        fragment PdpSharedDealerInfo_seller on Seller {
            agreementStart
            shippingAddress {
                displayCityStateCountry
            }
            analytics @include(if: $isClient) {
                metrics {
                    responseTime {
                        value
                    }
                }
            }
            tradeAssociations {
                serviceId
                displayName
            }
        }
    `,
});
